import { useRef } from 'react';
import { animate, useMotionValue, useReducedMotion } from 'motion/react';
import type { PanInfo } from 'motion/react';

/** Past this fraction of a step, releasing the drag commits to the next slide instead of springing back. */
const COMMIT_FRACTION = 0.25;
/** Flick assistance: velocity is worth this many seconds of travel when deciding where a drag lands. */
const VELOCITY_REACH = 0.2;
/** How far past either end the track will stretch before refusing, so the limits are felt, not hit. */
const RUBBER_BAND = 0.25;

/**
 * Horizontal drag-to-advance for the two About carousels (the milestone timeline and the annual
 * reports). Both are driven primarily by a year legend and secondarily by dragging, and both want the
 * same feel, so the gesture lives here rather than twice.
 *
 * Returns the motion value the track should translate by, the pan handlers to spread onto it, and the
 * `dragged` flag a click handler must check — the click that ends a drag would otherwise also fire.
 *
 * `step` is a CALLBACK, not a number: the step differs per breakpoint in both consumers, and measuring
 * it from the DOM at gesture end is what lets them avoid restating the breakpoint in JavaScript.
 */
export function useCarouselDrag(opts: {
	active: number;
	last: number;
	onChange: (index: number) => void;
	step: () => number;
}) {
	const reduce = useReducedMotion() ?? false;
	const drag = useMotionValue(0);
	/** Set once a pan has travelled far enough to be a drag, so the click it ends with is ignored. */
	const dragged = useRef(false);

	function onPan(_: PointerEvent, info: PanInfo) {
		if (Math.abs(info.offset.x) > 4) dragged.current = true;
		// Resist past the ends rather than refusing outright, so the first and last slides feel like limits.
		const beyond = (opts.active === 0 && info.offset.x > 0) || (opts.active === opts.last && info.offset.x < 0);
		drag.set(beyond ? info.offset.x * RUBBER_BAND : info.offset.x);
	}

	function onPanEnd(_: PointerEvent, info: PanInfo) {
		// ONE slide per gesture, however far or fast the drag went. Distance was the obvious thing to
		// divide by the step, but a flick carries 2000-4000px/s, so any velocity assistance at all sent a
		// single swipe three or four slides along. Velocity therefore only decides WHETHER the step
		// happens — so a short, quick flick still counts — and the legend is how you jump several years.
		const travel = info.offset.x + info.velocity.x * VELOCITY_REACH;
		const committed = Math.abs(travel) > opts.step() * COMMIT_FRACTION;
		const next = Math.min(Math.max(opts.active + (committed ? Math.sign(-travel) : 0), 0), opts.last);
		opts.onChange(next);

		// The slides jump to their new slots the moment the index changes, so the drag offset has to come
		// off in the same frame or the whole track would visibly slide back. Springing it looks smoother
		// when nothing changed hands (a rubber-banded pull at either end).
		if (next === opts.active && !reduce) animate(drag, 0, { type: 'spring', stiffness: 400, damping: 40 });
		else drag.set(0);

		// Cleared on a timeout, not here: the click event that ends a drag arrives after this handler.
		setTimeout(() => {
			dragged.current = false;
		}, 0);
	}

	return { drag, dragged, panHandlers: { onPan, onPanEnd } };
}

/**
 * `touch-action: pan-y` is what lets a phone still scroll the page through a draggable track: it hands
 * the horizontal gesture to us and keeps the vertical one for the browser. `touch-none`, which is what a
 * draggable normally reaches for, would swallow both.
 */
export const DRAG_TOUCH_ACTION = 'pan-y' as const;
